import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, LayoutGrid, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useOutletContext, useSearchParams } from "react-router-dom";
import type { AppSummary } from "@opsbelt/contract";

import { BlobShipMark } from "@/components/BlobShipMark";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { api } from "@/lib/api";
import { BLOBSHIP_APP_URL } from "@/lib/env";

/** Per-product launch URL — extend this map as more tools come online. */
const APP_URLS: Record<string, string> = {
  blobship: BLOBSHIP_APP_URL,
};
import type { MeContext } from "./AppShell";

function SeatManager({ productId }: { productId: string }) {
  const qc = useQueryClient();
  const seats = useQuery({
    queryKey: ["seats", productId],
    queryFn: () => api.appSeats(productId),
  });
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ["seats", productId] });
    qc.invalidateQueries({ queryKey: ["apps"] });
  };
  const assign = useMutation({
    mutationFn: (userId: string) => api.assignSeat(productId, userId),
    onSuccess: invalidate,
  });
  const unassign = useMutation({
    mutationFn: (userId: string) => api.unassignSeat(productId, userId),
    onSuccess: invalidate,
  });
  const err = assign.error ?? unassign.error;

  if (!seats.data) return null;
  const { seatsPurchased, seatsUsed, members } = seats.data;
  const full = seatsPurchased != null && seatsUsed >= seatsPurchased;

  return (
    <div className="mt-4 border-t border-border pt-4">
      <div className="mb-3 flex items-center justify-between">
        <span className="font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
          Seat assignment
        </span>
        <span className="font-mono text-[11px] text-muted-foreground">
          {seatsUsed} / {seatsPurchased ?? "∞"} seats used
        </span>
      </div>
      {err && (
        <p className="mb-2 text-xs text-destructive">
          {err instanceof Error ? err.message : "Failed"}
        </p>
      )}
      <div className="space-y-1">
        {members.map((m) => (
          <div
            key={m.userId}
            className="flex items-center gap-3 rounded-md px-2 py-1.5 hover:bg-secondary/50"
          >
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm">{m.name}</div>
              <div className="truncate text-xs text-muted-foreground">{m.email}</div>
            </div>
            <Badge variant="muted">{m.role}</Badge>
            {m.assigned ? (
              <Button
                size="sm"
                variant="ghost"
                onClick={() => unassign.mutate(m.userId)}
              >
                <Check className="size-3.5 text-success" /> Assigned
              </Button>
            ) : (
              <Button
                size="sm"
                variant="outline"
                disabled={full || assign.isPending}
                onClick={() => assign.mutate(m.userId)}
              >
                Assign
              </Button>
            )}
          </div>
        ))}
      </div>
      {full && (
        <p className="mt-2 text-xs text-muted-foreground">
          All seats used — add seats in Billing to assign more.
        </p>
      )}
    </div>
  );
}

export function Apps() {
  const { me } = useOutletContext<MeContext>();
  const navigate = useNavigate();
  const canManage = me?.org.role === "owner" || me?.org.role === "admin";
  const qc = useQueryClient();

  const apps = useQuery({ queryKey: ["apps"], queryFn: api.apps });
  const requests = useQuery({
    queryKey: ["access-requests"],
    queryFn: api.accessRequests,
    enabled: canManage,
  });
  const decide = useMutation({
    mutationFn: ({ id, decision }: { id: string; decision: "approve" | "deny" }) =>
      api.decideRequest(id, decision),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["access-requests"] });
      qc.invalidateQueries({ queryKey: ["apps"] });
      qc.invalidateQueries({ queryKey: ["seats"] });
    },
  });

  const [manage, setManage] = useState<string | null>(null);
  const pending = requests.data?.requests ?? [];

  // A product deep-links here with ?request=<id> (e.g. from BlobShip's no-seat
  // screen). Create the access request once, then clear the param + confirm.
  const [params, setParams] = useSearchParams();
  const [requested, setRequested] = useState<string | null>(null);
  const requestAccess = useMutation({
    mutationFn: (productId: string) => api.requestAccess(productId),
    onSuccess: (_d, productId) => {
      setRequested(productId);
      qc.invalidateQueries({ queryKey: ["access-requests"] });
    },
  });
  useEffect(() => {
    const p = params.get("request");
    if (p) {
      requestAccess.mutate(p);
      params.delete("request");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold tracking-tight">Apps</h1>
        <p className="mt-1 text-sm text-muted-foreground">
          Every tool on the belt — manage plans and who can use each one.
        </p>
      </div>

      {requested && (
        <div className="rounded-lg border border-success/30 bg-success/10 px-4 py-3 text-sm text-success">
          Access to <b>{requested}</b> requested — an admin will approve it.
        </div>
      )}

      {canManage && pending.length > 0 && (
        <Card>
          <CardContent className="p-5">
            <div className="mb-3 font-mono text-[11px] uppercase tracking-wider text-muted-foreground">
              Pending access requests
            </div>
            <div className="space-y-2">
              {pending.map((r) => (
                <div key={r.id} className="flex items-center gap-3">
                  <div className="min-w-0 flex-1 text-sm">
                    <span className="font-medium">{r.name || r.email}</span>{" "}
                    <span className="text-muted-foreground">
                      wants {r.productId}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={() => decide.mutate({ id: r.id, decision: "approve" })}
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => decide.mutate({ id: r.id, decision: "deny" })}
                  >
                    Deny
                  </Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        {apps.data?.apps.map((app) => (
          <AppCard
            key={app.productId}
            app={app}
            canManage={canManage}
            open={manage === app.productId}
            onManage={() =>
              setManage(manage === app.productId ? null : app.productId)
            }
            onSubscribe={() => navigate("/billing")}
          />
        ))}
      </div>
    </div>
  );
}

function AppCard({
  app,
  canManage,
  open,
  onManage,
  onSubscribe,
}: {
  app: AppSummary;
  canManage: boolean;
  open: boolean;
  onManage: () => void;
  onSubscribe: () => void;
}) {
  const subscribed = app.status !== "none";
  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-center gap-3">
          <span className="flex size-10 items-center justify-center rounded-xl bg-primary/10 text-primary">
            {app.productId === "blobship" ? (
              <BlobShipMark size={22} />
            ) : (
              <LayoutGrid className="size-5" />
            )}
          </span>
          <div className="flex-1">
            <div className="font-display font-semibold">{app.name}</div>
            <div className="font-mono text-[11px] text-muted-foreground">
              {subscribed
                ? `${app.seatsUsed} / ${app.seatsPurchased ?? "∞"} seats`
                : "not subscribed"}
            </div>
          </div>
          {subscribed ? (
            <Badge variant="success">active</Badge>
          ) : (
            <Badge variant="muted">free</Badge>
          )}
        </div>

        <div className="mt-4 flex gap-2">
          {subscribed && app.assignedToMe && (
            <Badge variant="primary" className="self-center">
              You have access
            </Badge>
          )}
          <div className="ml-auto flex gap-2">
            {app.assignedToMe && APP_URLS[app.productId] && (
              <a
                href={APP_URLS[app.productId]}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex h-8 items-center justify-center rounded-md bg-primary px-3 text-xs font-medium text-primary-foreground transition-colors hover:bg-primary/90"
              >
                Open {app.name}
              </a>
            )}
            {subscribed && canManage && (
              <Button size="sm" variant="outline" onClick={onManage}>
                {open ? "Done" : "Manage seats"}
              </Button>
            )}
            {!subscribed && canManage && (
              <Button size="sm" onClick={onSubscribe}>
                <Plus className="size-3.5" /> Subscribe
              </Button>
            )}
          </div>
        </div>

        {open && canManage && <SeatManager productId={app.productId} />}
      </CardContent>
    </Card>
  );
}
